"use client";

import { useEffect, useState } from "react";
import { useI18n } from "@/providers/i18n";
import { RefreshCcw, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { checkAppVersion } from "@/lib/version";

const CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes

export function VersionChecker() {
  const { t } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [override, setOverride] = useState<{
    // i18n keys (preferred)
    titleKey?: string;
    descriptionKey?: string;
    buttonLabelKey?: string;
    ignoreLabelKey?: string;
    titleVars?: Record<string, unknown>;
    descriptionVars?: Record<string, unknown>;
    buttonLabelVars?: Record<string, unknown>;
    ignoreLabelVars?: Record<string, unknown>;
    allowIgnore?: boolean;
  } | null>(null);

  // When closing the modal (e.g. clicking "Ignore"), keep override content
  // until the dialog is actually closed to avoid a 1-frame flash back to
  // the default "new version" copy during exit animations.
  useEffect(() => {
    if (!showModal) {
      setOverride(null);
      setIsRefreshing(false);
    }
  }, [showModal]);

  useEffect(() => {
    const checkVersion = async () => {
      const hasNewVersion = await checkAppVersion();
      if (hasNewVersion) {
        setOverride(null);
        setShowModal(true);
      }
    };

    // Check on mount (delay slightly to allow app to settle)
    const timeout = setTimeout(checkVersion, 5000);

    // Check periodically
    const interval = setInterval(checkVersion, CHECK_INTERVAL);

    // Check on window focus
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);

    // Check on custom event (background check)
    const onCustomCheck = () => checkVersion();
    window.addEventListener("check_version", onCustomCheck);

    // Force show modal event (when handleError detects version mismatch)
    const onShowModal = () => {
      setOverride(null);
      setShowModal(true);
    };
    window.addEventListener("show_version_modal", onShowModal);

    // Generic refresh modal (e.g. save failure)
    const onShowRefreshModal = (e: any) => {
      try {
        const detail = (e as CustomEvent)?.detail || {};
        setOverride({
          titleKey: detail?.titleKey,
          descriptionKey: detail?.descriptionKey,
          buttonLabelKey: detail?.buttonLabelKey,
          ignoreLabelKey: detail?.ignoreLabelKey,
          titleVars: detail?.titleVars,
          descriptionVars: detail?.descriptionVars,
          buttonLabelVars: detail?.buttonLabelVars,
          ignoreLabelVars: detail?.ignoreLabelVars,
          allowIgnore: Boolean(detail?.allowIgnore),
        });
      } catch {
        setOverride({
          titleKey: undefined,
          descriptionKey: undefined,
          buttonLabelKey: undefined,
          ignoreLabelKey: undefined,
          titleVars: undefined,
          descriptionVars: undefined,
          buttonLabelVars: undefined,
          ignoreLabelVars: undefined,
          allowIgnore: undefined,
        });
      }
      setShowModal(true);
    };
    window.addEventListener("show_refresh_modal", onShowRefreshModal as any);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("check_version", onCustomCheck);
      window.removeEventListener("show_version_modal", onShowModal);
      window.removeEventListener("show_refresh_modal", onShowRefreshModal as any);
    };
  }, []);

  return (
    <Dialog open={showModal} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md bg-sidebar border border-sidebar-border shadow-lg p-0 gap-0 overflow-hidden rounded-xl [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-8 pb-2 bg-sidebar border-none flex flex-col items-center text-center">
          <div className="p-3 bg-primary/10 rounded-full mb-4 w-fit">
            <RefreshCcw className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {override?.titleKey
              ? t(override.titleKey, override.titleVars)
              : t("version.newVersionAvailable")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground pt-2">
            {override?.descriptionKey
              ? t(override.descriptionKey, override.descriptionVars)
              : t("version.description")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="p-6 pt-4 bg-sidebar">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                setIsRefreshing(true);
                window.location.reload();
              }}
              disabled={isRefreshing}
              className={override?.allowIgnore ? "flex-1 h-10 font-semibold" : "w-full h-10 font-semibold"}
            >
              {isRefreshing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {override?.buttonLabelKey
                ? t(override.buttonLabelKey, override.buttonLabelVars)
                : t("version.refreshNow")}
            </Button>
            {override?.allowIgnore ? (
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                }}
                disabled={isRefreshing}
                className="flex-1 h-10 font-semibold"
              >
                {override?.ignoreLabelKey
                  ? t(override.ignoreLabelKey, override.ignoreLabelVars)
                  : t("refreshModal.ignore")}
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}